import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SubmitBar from '../SubmitBar';

/**
 * QuestionCard
 * Applies a consistent top gap under the sticky header and renders the
 * provided question content. Always renders a SubmitBar beneath the content.
 *
 * Props:
 *  - className?: string
 *  - style?: React.CSSProperties
 *  - children?: React.ReactNode  (question content)
 *  - viewportRef?: React.RefObject<HTMLElement> (optional - for future use/precision)
 *  - stepKey?: string | number (unique per question; used to reset scroll on step change)
 *  - onBack: () => void
 *  - onNext: () => void
 *  - nextLabel: string
 *  - disabled?: boolean
 *  - hideBack?: boolean
 *  - disableBack?: boolean
 *  - nextLoading?: boolean
 *  - nextLoadingLabel?: string - Custom loading label (defaults to SubmitBar's default)
 */
export default function QuestionCard({
  className = '',
  style = {},
  children,
  viewportRef,
  stepKey,
  onBack,
  onNext,
  nextLabel = 'Next',
  disabled = false,
  hideBack = false,
  disableBack = false,
  nextLoading = false,
  nextLoadingLabel,
  // Optional preamble text rendered above the question title (e.g. framing question for HFP forms)
  questionPreamble = '',
  // Optional status shown at the start of the action row (Harbour: the save
  // status beside Back and Next, as the question frames place it).
  footerStatus = null,
  // 'harbour' styles the Back / Next buttons as the Harbour question frames do.
  submitVariant = 'default',
  // Optional content drawn just below the card (Harbour: the "save as you go"
  // reassurance, centred under the card as the question frames place it).
  belowCard = null,
  // Render the question's title within the main content area (above inputs)
  questionTitle = '',
  // Optional supporting description rendered under the title (from the schema's
  // question.description). Gives guidance/reassurance beneath the prompt for any
  // question type, so authored help text shows consistently rather than only in
  // the handful of field components that render it themselves.
  questionDescription = '',
  // Optional information line drawn above the question (e.g. what the school
  // referred the child for, at the start of the parent's Reason for Referral).
  questionNotice = null,
}) {
  // Internal scrollable region; only enabled if content exceeds available space
  const scrollableRef = useRef(null);
  const contentRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  // Whether the scroll position is at (or near) the end of the content.
  // Drives the bottom fade affordance: visible while more content remains
  // below the fold, hidden once the user has scrolled to the end.
  const [atEnd, setAtEnd] = useState(true);

  // Small tolerance for fractional scroll positions (zoomed browsers report
  // non-integer scrollTop values that never quite reach the exact end).
  const isScrolledToEnd = (el) =>
    (el.scrollTop || 0) + (el.clientHeight || 0) >= (el.scrollHeight || 0) - 8;

  // Helper: recompute if content overflows
  const recomputeOverflow = () => {
    const el = scrollableRef.current;
    if (!el) return;
    // If content is taller than the container, we need to enable scrolling
    const overflow = (el.scrollHeight || 0) > (el.clientHeight || 0);
    setNeedsScroll(overflow);
    setAtEnd(!overflow || isScrolledToEnd(el));
  };

  // Track scroll position so the bottom fade disappears at the end of content
  const handleScroll = () => {
    const el = scrollableRef.current;
    if (!el) return;
    setAtEnd(isScrolledToEnd(el));
  };

  // Reset scroll top whenever this card mounts/enters
  useEffect(() => {
    const el = scrollableRef.current;
    if (el) el.scrollTop = 0;
    // Recompute overflow after resetting to ensure correct state for new step
    recomputeOverflow();
  }, []);

  // Also reset scroll when stepKey changes (navigating between questions)
  useEffect(() => {
    const el = scrollableRef.current;
    if (el) el.scrollTop = 0;
    recomputeOverflow();
    // Belt-and-braces for the intermittent measurement race (TD-138): the
    // synchronous recompute above can run before the layout-metrics CSS vars
    // (header/footer heights) have settled, leaving needsScroll stale. Re-check
    // after the next paint and once more shortly after so a tall first step
    // always becomes scrollable and the Next button stays reachable.
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame(() => recomputeOverflow())
      : null;
    const t = setTimeout(() => recomputeOverflow(), 150);
    return () => {
      if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [stepKey]);

  // Recompute after first layout and whenever children size changes
  useLayoutEffect(() => {
    recomputeOverflow();
    // Observe size changes of BOTH the inner content AND the scrollable
    // container. The container's height is driven by --rms-viewport-height
    // (100vh - header - footer); useLayoutMetrics measures the header/footer
    // *after* first paint, which shrinks the container. Observing only the
    // content misses that: the container becomes shorter than its content but
    // `needsScroll` never re-evaluates, so overflow stays hidden and the lower
    // fields + Next button get clipped with no way to scroll (TD-138).
    const contentEl = contentRef.current;
    const scrollEl = scrollableRef.current;
    const RO = typeof window !== 'undefined' ? window.ResizeObserver : null;
    let obs = null;
    if (RO) {
      obs = new RO(() => recomputeOverflow());
      try { if (contentEl) obs.observe(contentEl); } catch (_) {}
      try { if (scrollEl) obs.observe(scrollEl); } catch (_) {}
    }
    const onWin = () => recomputeOverflow();
    window.addEventListener('resize', onWin);
    window.addEventListener('orientationchange', onWin);
    if (typeof window !== 'undefined' && window.visualViewport) {
      try {
        window.visualViewport.addEventListener('resize', onWin);
        window.visualViewport.addEventListener('scroll', onWin);
      } catch (_) {}
    }
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('orientationchange', onWin);
      try { if (obs) obs.disconnect(); } catch (_) {}
      try {
        if (typeof window !== 'undefined' && window.visualViewport) {
          window.visualViewport.removeEventListener('resize', onWin);
          window.visualViewport.removeEventListener('scroll', onWin);
        }
      } catch (_) {}
    };
  }, []);

  return (
    <div className={`relative text-left rms-question-card ${className}`} style={style}>
      <div
        ref={scrollableRef}
        // Marker attribute lets the surrounding layout forward wheel events to
        // the active card's scroll area (see rms/utils/cardScrollForwarding.js).
        data-rms-card-scroll="true"
        // Visible slim scrollbar when scrolling is needed (UAT feedback: the
        // previously hidden scrollbar made overflow content undiscoverable).
        className={`${needsScroll ? 'rms-card-scroll' : ''}`}
        style={{
          maxHeight: 'calc(var(--rms-viewport-height) - var(--rms-question-top-gap))',
          overflowY: needsScroll ? 'auto' : 'hidden',
          overscrollBehavior: 'contain',
          // Use touch-action to disable touch scrolling when not needed (prevents passive listener warnings)
          touchAction: needsScroll ? 'auto' : 'none',
        }}
        onScroll={handleScroll}
        aria-describedby={needsScroll ? 'rms-card-scroll-hint' : undefined}
      >
        {/* Top gap using CSS variable populated by useLayoutMetrics */}
        <div style={{ height: 'var(--rms-question-top-gap)' }} />
        {/* Content wrapper for ResizeObserver */}
        <div ref={contentRef} className="space-y-4">
          {questionNotice}
          {/* Optional framing question shown above the statement (e.g. HFP forms) */}
          {questionPreamble ? (
            <p className="rms-question-preamble text-sm md:text-base font-medium text-gray-500 italic">
              {questionPreamble}
            </p>
          ) : null}
          {/* Present the question prompt contiguous with its inputs (H2 for hierarchy). */}
          {questionTitle ? (
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              {questionTitle}
            </h2>
          ) : null}
          {/* Supporting description/guidance beneath the prompt (schema-authored). */}
          {questionDescription ? (
            <p className="text-sm md:text-base text-gray-600">
              {questionDescription}
            </p>
          ) : null}
          {children}
        </div>
        <div className={`mt-6 ${footerStatus ? 'rms-card-actions' : ''}`.trim()}>
          {footerStatus ? <div className="rms-card-actions__status">{footerStatus}</div> : null}
          <div className="rms-card-submit">
            <SubmitBar
              onBack={onBack}
              onNext={onNext}
              nextLabel={nextLabel}
              disabled={disabled}
              hideBack={hideBack}
              disableBack={disableBack}
              loading={nextLoading}
              loadingLabel={nextLoadingLabel}
              variant={submitVariant}
            />
          </div>
        </div>
      </div>
      {belowCard ? <div className="rms-question-card__below">{belowCard}</div> : null}
      {/* Bottom fade indicating more content below; hidden once scrolled to the end */}
      {needsScroll && !atEnd ? (
        <div aria-hidden="true" data-testid="rms-card-scroll-fade" className="rms-card-scroll-fade" />
      ) : null}
      {/* Screen-reader hint when scrolling is available */}
      {needsScroll ? (
        <div id="rms-card-scroll-hint" className="sr-only" aria-live="polite" aria-atomic="true">
          This question content is scrollable.
        </div>
      ) : null}
    </div>
  );
}

